'use client';
import React, { useState, useEffect } from 'react';
import Button from '@src/v3/components/coreUI/button';
import { RadioGroup, RadioGroupItem } from '@src/v3/components/coreUI/radio';
import Icon from '@src/v3/components/coreUI/icon';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import {
  RegisterStepProps,
  UserType,
} from '@src/v3/interfaces/components/registerModal';
import GradientPanelSvg from '@src/v3/assets/svg/gradientPanel';
import { ZEME_USERS_ROLE, REGISTER_STEPS } from '@src/v3/constants/configs';
import { VALIDATION_MESSAGES } from '@src/v3/constants/messages';

const UserTypeStep: React.FC<RegisterStepProps> = ({
  onUserTypeSelect,
  selectedUserType,
  isLoading = false,
  onStepChange,
  onOpenLoginModal,
  onClose,
  userTypeError,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (userTypeError) {
      setErrorMessage(userTypeError);
    }
  }, [userTypeError]);
  const userTypes = [
    {
      id: ZEME_USERS_ROLE.RENTER,
      label: 'Renter or Guarantor',
    },
    {
      id: ZEME_USERS_ROLE.AGENT,
      label: 'Agent or Broker',
    },
    {
      id: ZEME_USERS_ROLE.LANDLORD,
      label: 'Landlord or Property Manager',
    },
  ];

  const handleUserTypeSelect = (userType: UserType) => {
    onUserTypeSelect?.(userType);
  };

  const handleLoginClick = () => {
    if (onOpenLoginModal) {
      onOpenLoginModal();
    }
  };

  const handleContinue = () => {
    if (selectedUserType && onStepChange) {
      setErrorMessage(''); // Clear error on successful navigation
      onStepChange(REGISTER_STEPS.PHONE);
    } else {
      setErrorMessage(VALIDATION_MESSAGES.selectUserType);
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Left Section - White Panel */}
      <div className="md:w-[686px] w-full h-full md:h-[790px] self-stretch px-0 md:px-2.5 pt-0 md:pt-2.5 pb-0 md:pb-8 bg-white rounded-none md:rounded-l-2xl flex flex-col justify-between items-start relative">
        {/* Mobile Header */}
        <div className="md:hidden w-full">
          <DialogHeader className="px-5 pt-6 pb-5 flex flex-col gap-3 items-start">
            <div className="flex flex-col gap-4 items-start w-full">
              <div className="flex items-center gap-2.5 w-full">
                <div className="flex-1 text-gray-1000 text-2xl font-medium font-geist leading-[36px] tracking-[-0.48px]">
                  Are you registering as a
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex self-stretch flex-col justify-start items-start gap-2.5">
          <div className="self-stretch px-8 pt-16 pb-5 bg-white flex flex-col justify-center items-start gap-3">
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="flex-1 justify-start text-gray-1000 text-2xl font-medium font-geist leading-9">
                  Are you registering as a
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 w-full flex flex-col overflow-y-auto pb-[100px] md:pb-0">
          {/* Mobile: Radio Options */}
          <div className="md:hidden px-5 py-3 flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <RadioGroup
                value={selectedUserType || ''}
                onValueChange={(value) =>
                  handleUserTypeSelect(value as UserType)
                }
                className="flex flex-col w-full gap-4"
              >
                {userTypes.map((type) => (
                  <label
                    key={type.id}
                    htmlFor={type.id}
                    className={`w-full h-[60px] px-4 py-6 rounded-xl border flex items-center gap-3 cursor-pointer transition-all ${
                      selectedUserType === type.id
                        ? 'border-gray-1000'
                        : 'border-gray-400'
                    }`}
                  >
                    <RadioGroupItem
                      value={type.id}
                      id={type.id}
                      className={`!w-6 !h-6 !min-w-6 !min-h-6 rounded-full border focus-visible:outline-none shrink-0 ${
                        selectedUserType === type.id
                          ? 'border-gray-1000'
                          : 'border-[#C9C9C9]'
                      } [&>div]:!w-3 [&>div]:!h-3`}
                    />
                    <div className="flex flex-col justify-start items-start">
                      <div className="text-gray-1000 text-base font-medium font-geist leading-normal tracking-[-0.16px]">
                        {type.label}
                      </div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Error Message - Mobile */}
            {errorMessage && (
              <div className="inline-flex justify-start items-start gap-2">
                <Icon
                  iconName="warningCircle"
                  iconProps={{ className: 'w-5 h-5 flex-shrink-0 mt-0.5' }}
                />
                <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                  {errorMessage}
                </span>
              </div>
            )}

            {/* Login Link - Mobile */}
            <Button
              type="button"
              variant="link"
              className="w-full justify-start text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 text-left tracking-[-0.14px] h-auto p-0"
              onClick={handleLoginClick}
            >
              Have an account? Log in
            </Button>
          </div>

          {/* Desktop: Radio Options + Button Section */}
          <div className="hidden md:flex self-stretch px-8 py-2.5 flex-col justify-center items-end gap-4">
            <RadioGroup
              value={selectedUserType || ''}
              onValueChange={(value) => handleUserTypeSelect(value as UserType)}
              className="flex flex-col items-end w-full gap-4"
            >
              {userTypes.map((type) => (
                <label
                  key={type.id}
                  htmlFor={type.id}
                  className={`self-stretch h-20 px-4 py-6 rounded-xl border ${
                    selectedUserType === type.id
                      ? 'border-gray-1000'
                      : 'border-gray-200'
                  } inline-flex justify-start items-center gap-3 cursor-pointer hover:border-gray-1000 transition-all`}
                >
                  <RadioGroupItem
                    value={type.id}
                    id={type.id}
                    className="flex w-[18px] h-[18px] focus-visible:outline-none p-1 justify-center items-center gap-[10px] rounded-full border border-[#E5E7EB]"
                  />
                  <div className="inline-flex flex-col justify-start items-start gap-3">
                    <div className="justify-start text-gray-1000 text-base font-medium font-geist leading-7">
                      {type.label}
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>

            {/* Error Message - Desktop */}
            {errorMessage && (
              <div className="self-stretch inline-flex justify-start items-start gap-2">
                <Icon
                  iconName="warningCircle"
                  iconProps={{ className: 'w-5 h-5 flex-shrink-0 mt-0.5' }}
                />
                <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                  {errorMessage}
                </span>
              </div>
            )}

            {/* Continue Button - Desktop */}
            <Button
              type="button"
              variant="primary"
              size="xl"
              className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
              disabled={!selectedUserType || isLoading}
              onClick={handleContinue}
            >
              <div className="justify-start text-white text-base font-medium font-geist leading-7">
                Continue
              </div>
              <Icon
                iconName="arrowRight"
                iconProps={{ className: 'size-5 text-white' }}
              />
            </Button>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="md:hidden absolute bottom-0 left-0 right-0 w-full px-5 py-5 bg-white shadow-[0px_-1px_20px_0px_rgba(0,0,0,0.05)] border-b border-gray-400 inline-flex justify-center items-start gap-6 z-10">
          <Button
            type="button"
            onClick={handleBack}
            className="h-12 px-8 py-2.5 bg-background-200 hover:bg-background-200 rounded-[36px] flex justify-start items-center gap-2.5 shrink-0"
          >
            <span className="justify-start text-gray-1000 text-base font-semibold font-geist">
              Back
            </span>
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            variant="primary"
            disabled={!selectedUserType || isLoading}
            className="flex-1 h-12 px-8 py-2.5 bg-focus rounded-[36px] flex justify-center items-center min-w-0"
          >
            <span className="justify-start text-white text-base font-semibold font-geist">
              Continue
            </span>
            <Icon
              iconName="arrowRight"
              iconProps={{ className: 'w-5 h-5 text-white' }}
            />
          </Button>
        </div>

        {/* Desktop: Login Link Section */}
        <div className="hidden md:flex self-stretch px-8 justify-start items-center gap-24">
          <Button
            type="button"
            variant="link"
            className="justify-start text-focus text-sm font-normal font-geist leading-6 hover:opacity-75 h-auto p-0"
            onClick={handleLoginClick}
          >
            Have an account? Log in
          </Button>
        </div>
      </div>
      {/* Right Section - Gradient Background */}
      <div className="hidden md:flex relative flex-col p-2.5 items-start gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
        <GradientPanelSvg
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
        />
      </div>
    </div>
  );
};

export default UserTypeStep;
